import { css, html, nothing } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

const ZOOM_STEPS = [0, 0.5, 1, 1.5, 2, 3, 4];

class ImageLightbox extends BaseComponent {
    static properties = {
        src: { type: String },
        _zoom: { type: Number, state: true },
        _isPanning: { type: Boolean, state: true },
        _naturalWidth: { type: Number, state: true },
        _naturalHeight: { type: Number, state: true },
    };

    static styles = css`
        :host { display: contents; }
        .overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.85);
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            z-index: 9999;
        }
        .close-btn {
            position: absolute; top: 1rem; right: 1rem; z-index: 10001;
            background: rgba(255,255,255,0.15); border: none; color: white;
            width: 2.5rem; height: 2.5rem; border-radius: 50%;
            font-size: 1.25rem; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
        }
        .close-btn:hover { background: rgba(255,255,255,0.3); }
        .viewport {
            overflow: auto; max-width: 90vw; max-height: 85vh;
            cursor: grab;
        }
        .viewport.panning { cursor: grabbing; }
        .viewport img {
            display: block; border-radius: 4px; margin: auto;
            user-select: none; -webkit-user-drag: none;
        }
        .toolbar {
            position: absolute; bottom: 1.5rem; left: 50%; transform: translateX(-50%);
            display: flex; align-items: center; gap: 0.5rem; z-index: 10001;
            background: rgba(0,0,0,0.6); border-radius: 2rem; padding: 0.4rem 0.75rem;
        }
        .toolbar button {
            background: rgba(255,255,255,0.15); border: none; color: white;
            width: 2rem; height: 2rem; border-radius: 50%; cursor: pointer;
            display: flex; align-items: center; justify-content: center; font-size: 1rem;
        }
        .toolbar button:hover { background: rgba(255,255,255,0.3); }
        .toolbar button:disabled { opacity: 0.3; cursor: default; }
        .toolbar button:disabled:hover { background: rgba(255,255,255,0.15); }
        .zoom-label { color: white; font-size: 0.8rem; min-width: 3rem; text-align: center; }
    `;

    constructor() {
        super();
        this.src = '';
        this._zoom = 0;
        this._isPanning = false;
        this._naturalWidth = 0;
        this._naturalHeight = 0;
        this._panStart = { x: 0, y: 0 };
        this._scrollStart = { left: 0, top: 0 };
        this._onKeyDown = this._onKeyDown.bind(this);
    }

    async connectedCallback() {
        super.connectedCallback();
        document.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown(e) {
        if (!this.src) return;
        if (e.key === 'Escape') {
            this._close();
        }
    }

    _close() {
        this._zoom = 0;
        this.dispatchEvent(new CustomEvent('close'));
    }

    _onImageLoad(e) {
        this._naturalWidth = e.target.naturalWidth;
        this._naturalHeight = e.target.naturalHeight;
    }

    _getZoomIndex() {
        return ZOOM_STEPS.indexOf(this._zoom);
    }

    _zoomIn() {
        const idx = this._getZoomIndex();
        if (idx < ZOOM_STEPS.length - 1) {
            this._zoom = ZOOM_STEPS[idx + 1];
        }
    }

    _zoomOut() {
        const idx = this._getZoomIndex();
        if (idx > 0) {
            this._zoom = ZOOM_STEPS[idx - 1];
        }
    }

    _zoomFit() {
        this._zoom = 0;
    }

    _onWheel(e) {
        e.preventDefault();
        if (e.deltaY < 0) {
            this._zoomIn();
        } else {
            this._zoomOut();
        }
    }

    _onPointerDown(e) {
        if (this._zoom === 0) return;
        const viewport = e.currentTarget;
        if (
            viewport.scrollWidth <= viewport.clientWidth &&
            viewport.scrollHeight <= viewport.clientHeight
        )
            return;
        this._isPanning = true;
        this._panStart = { x: e.clientX, y: e.clientY };
        this._scrollStart = { left: viewport.scrollLeft, top: viewport.scrollTop };
        viewport.setPointerCapture(e.pointerId);
    }

    _onPointerMove(e) {
        if (!this._isPanning) return;
        const viewport = e.currentTarget;
        const dx = e.clientX - this._panStart.x;
        const dy = e.clientY - this._panStart.y;
        viewport.scrollLeft = this._scrollStart.left - dx;
        viewport.scrollTop = this._scrollStart.top - dy;
    }

    _onPointerUp(e) {
        if (!this._isPanning) return;
        this._isPanning = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
    }

    _getImageStyle() {
        if (this._zoom === 0 || !this._naturalWidth || !this._naturalHeight) {
            return `max-width: 90vw; max-height: 85vh;`;
        }
        const w = this._naturalWidth * this._zoom;
        const h = this._naturalHeight * this._zoom;
        return `width: ${w}px; height: ${h}px; max-width: none; max-height: none;`;
    }

    _getZoomLabel() {
        if (this._zoom === 0) return 'Fit';
        return `${Math.round(this._zoom * 100)}%`;
    }

    render() {
        if (!this.src) return nothing;

        const zoomIdx = this._getZoomIndex();

        return html`
            <div class="overlay" @click="${(e) => {
                if (e.target === e.currentTarget) this._close();
            }}">
                <button class="close-btn" @click="${(e) => {
                    e.stopPropagation();
                    this._close();
                }}">
                    <i class="bi bi-x-lg"></i>
                </button>
                <div class="viewport ${this._isPanning ? 'panning' : ''}"
                     @wheel="${this._onWheel}"
                     @pointerdown="${this._onPointerDown}"
                     @pointermove="${this._onPointerMove}"
                     @pointerup="${this._onPointerUp}">
                    <img src="${this.src}" alt="" style="${this._getImageStyle()}"
                         @load="${this._onImageLoad}" draggable="false">
                </div>
                <div class="toolbar" @click="${(e) => e.stopPropagation()}">
                    <button ?disabled="${zoomIdx <= 0}" @click="${this._zoomOut}">
                        <i class="bi bi-dash"></i>
                    </button>
                    <span class="zoom-label">${this._getZoomLabel()}</span>
                    <button ?disabled="${zoomIdx >= ZOOM_STEPS.length - 1}" @click="${this._zoomIn}">
                        <i class="bi bi-plus"></i>
                    </button>
                    <button @click="${this._zoomFit}" ?disabled="${this._zoom === 0}">Fit</button>
                </div>
            </div>
        `;
    }
}

customElements.define('image-lightbox', ImageLightbox);
